/**
 * @fileoverview News Autocomplete Setup Module.
 * This module configures the Algolia Autocomplete functionality specifically for news content.
 * It includes custom rendering, event handling, and UI modifications for the news autocomplete feature.
 */

import algoliasearch from 'algoliasearch/lite';
import { autocomplete } from '@algolia/autocomplete-js';
import { elementExists, getFullIndexName } from "../Utils";
import { AutocompleteNewsItemTemplate } from "./widgets/AutocompleteNewsItemTemplate";

/**
 * @constant
 * @type {object}
 * @description Algolia search client instance configured with application ID and search-only API key.
 */
const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);

const urlParams = new URLSearchParams(window.location.search);
const initialQuery = urlParams.get('q');
const newsAutocompleteContainer = '#news-autocomplete';
const indexName = 'post';
const fullIndexName = getFullIndexName(indexName);

/**
 * @constant
 * @type {object}
 * @description Class names used for styling various parts of the news autocomplete component.
 */
const classNames = {
    detachedCancelButton: 'pxl-newsDetachedCancelButton',
    detachedFormContainer: 'pxl-newsDetachedFormContainer',
    detachedContainer: 'pxl-newsDetachedContainer',
    detachedOverlay: 'pxl-newsDetachedOverlay',
    detachedSearchButton: 'pxl-newsDetachedSearchButton',
    detachedSearchButtonIcon: 'pxl-newsDetachedSearchButtonIcon',
    detachedSearchButtonPlaceholder: 'pxl-newsDetachedSearchButtonPlaceholder',
    detachedSearchButtonQuery: 'pxl-newsDetachedSearchButtonQuery',
    form: 'pxl-newsForm',
    input: 'pxl-newsInput',
    inputWrapper: 'pxl-newsInputWrapper',
    inputWrapperPrefix: 'pxl-newsInputWrapperPrefix',
    inputWrapperSuffix: 'pxl-newsInputWrapperSuffix',
    item: 'pxl-newsItem',
    label: 'pxl-newsLabel',
    list: 'pxl-newsList',
    loadingIndicator: 'pxl-newsLoadingIndicator',
    panel: 'pxl-newsPanel',
    panelLayout: 'pxl-newsPanelLayout',
    clearButton: 'pxl-newsClearButton',
    root: 'pxl-newsRoot',
    source: 'pxl-newsSource',
    sourceFooter: 'pxl-newsSourceFooter',
    sourceHeader: 'pxl-newsSourceHeader',
    sourceNoResults: 'pxl-newsSourceNoResults',
    submitButton: 'pxl-newsSubmitButton'
};

if (elementExists(newsAutocompleteContainer)) {
    init();
}

/**
 * Initializes the news autocomplete functionality.
 * Configures Algolia autocomplete with custom templates and behaviors.
 */
function init() {
    window.newsAutocomplete = autocomplete({
        container: newsAutocompleteContainer,
        placeholder: 'Search news',
        openOnFocus: true,
        classNames,
        detachedMediaQuery: '(max-width: 768px)',
        initialState: {
            query: initialQuery || '',
        },
        getSources() {
            return [
                {
                    sourceId: fullIndexName,
                    getItems({ query }) {
                        return searchClient
                            .initIndex(fullIndexName)
                            .search(query, {
                                attributesToRetrieve: [
                                    'title',
                                    'guid',
                                    'featured_image',
                                    'post_excerpt',
                                    'website',
                                    'formattedDate',
                                    'permalink'
                                ],
                                hitsPerPage: 5,
                            })
                            .then(({ hits }) => hits);
                    },
                    templates: {
                        item: AutocompleteNewsItemTemplate
                    },
                    onSelect({ item, setContext }) {
                        setContext({ url: item.guid });
                    },
                }
            ];
        },
        onSubmit({ state }) {
            const query = state.query;
            if (!query.trim()) {
                return;
            }
            window.location.href = `/news-search-results?q=${encodeURIComponent(query)}`;
        },
    });

    /**
     * MutationObserver to observe DOM mutations for applying custom behaviors to dynamically added elements.
     */
    const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                for (let node of mutation.addedNodes) {
                    if (node instanceof Element) {
                        const mobileInput = node.querySelector('.pxl-newsDetachedFormContainer input.pxl-newsInput');

                        if (mobileInput) {
                            addCrossIconWithCancelButtonBehavior(mobileInput);
                        }
                    }
                }
            }
        }
    });

    /**
     * Adds a custom cross icon with cancel button behavior to the specified input element.
     * @param {HTMLElement} inputElement - The input element to add the cross icon to.
     */
    function addCrossIconWithCancelButtonBehavior(inputElement) {
        if (inputElement.parentNode.querySelector('.custom-cross-icon')) {
            return;
        }

        // Create the main SVG element
        const crossIcon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        crossIcon.setAttribute("width", "32");
        crossIcon.setAttribute("height", "33");
        crossIcon.setAttribute("viewBox", "0 0 32 33");
        crossIcon.setAttribute("fill", "none");
        crossIcon.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        crossIcon.classList.add('custom-cross-icon');

        // Create the rectangle
        const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        rect.setAttribute("y", "0.898438");
        rect.setAttribute("width", "32");
        rect.setAttribute("height", "32");
        rect.setAttribute("fill", "#F9771C");
        crossIcon.appendChild(rect);

        // Create the path
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", "M16.0003 15.4475L21.2816 10.0191C21.6712 9.61862 22.303 9.61862 22.6927 10.0191C23.0823 10.4197 23.0823 11.069 22.6927 11.4696L17.4114 16.898L22.6927 22.3264C23.0823 22.7269 23.0823 23.3763 22.6927 23.7768C22.303 24.1773 21.6712 24.1773 21.2816 23.7768L16.0003 18.3484L10.719 23.7768C10.3293 24.1773 9.69754 24.1773 9.30788 23.7768C8.91821 23.3763 8.91821 22.7269 9.30788 22.3264L14.5892 16.898L9.30788 11.4696C8.91821 11.069 8.91821 10.4197 9.30788 10.0191C9.69754 9.61862 10.3293 9.61862 10.719 10.0191L16.0003 15.4475Z");
        path.setAttribute("fill", "white");
        crossIcon.appendChild(path);

        // Style and behavior are retained from your original code
        crossIcon.style.position = 'absolute';
        crossIcon.style.right = '10px';
        crossIcon.style.top = '-35px';
        crossIcon.style.transform = 'translateY(-50%)';
        crossIcon.style.cursor = 'pointer';

        crossIcon.addEventListener('click', function () {
            const cancelButton = document.querySelector('.pxl-newsDetachedCancelButton');
            if (cancelButton) {
                cancelButton.click();
            }
        });

        inputElement.parentNode.appendChild(crossIcon);
    }


    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}